import { EventType } from "@/analytics/analytics-events";
import { CreateAreaEventProperties } from "@/analytics/create-area-workflow-events-properties";
import { ImportAreaSheetProperties } from "@/analytics/generic-events-properties";
import { useFileUploaderWithPromise } from "@/components/common/file-upload-context/use-file-uploader";
import { UploadElementType } from "@/components/common/point-cloud-file-upload-context/use-upload-element-types";
import {
  resetAreaImgSheets,
  setNumberOfSheets,
} from "@/store/modes/import-sheet-mode-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  Alert,
  blue,
  FaroButton,
  FaroText,
  getFileExtension,
  grey,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { Box, Stack, SxProps, Theme } from "@mui/material";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  OpenImageFileSelectControl,
  SheetImagePreview,
  SheetUploadControls,
} from "./create-area-body-parts";
import {
  MAX_ALLOWED_IMG_HEIGHT,
  MAX_ALLOWED_IMG_WIDTH,
  SourcePdfInfoNoPageNumber,
} from "./create-area-utils";
import { PdfPageThumbnailList } from "./pdf-page-thumbnail-list";
import {
  GetSourcePdfFileData,
  useCreateAreaLogic,
} from "./use-create-area-logic";

type CreateAreaMainBodyProps = {
  /** Optional styling applied to the component */
  sx?: SxProps<Theme>;

  /** true if sheet is a first one for the project */
  isFirstSheet?: boolean;
};

// The default size of the tiles if the image is an image sheet tiled
const IMAGE_SHEET_TILES_SIZE = 1024;

// The resolution to use for the image sheet tiled when user ask for a higher resolution
const HIGH_IMAGE_SHEET_TILED_RESOLUTION = 32768;

/**
 * @returns a component containing the input element to choose the name of the image,
 * the file drop handler and the button to create the sheet
 */
export function CreateAreaMainBody({
  sx,
  isFirstSheet = false,
}: CreateAreaMainBodyProps): JSX.Element {
  const {
    previewImageFile,
    selectedFile,
    inputName,
    inputNameError,
    hasDefaultGrid,
    isImageOversized,
    setInputName,
    setFile,
    setDefaultGrid,
    createImageSheetTiled,
    createSheet,
    cancelUpload,
    widthToHeightRatio,
    rotation,
    setRotation,
    crop,
    setCrop,
    existingArea,
    numberOfPdfPages,
    onUpdateSelectedPdfPage,
    selectedPdfPage: previewedPdfPage,
  } = useCreateAreaLogic();

  const [useImprovedImageResolution, setUseImprovedImageResolution] =
    useState(false);

  const [selectedPages, setSelectedPages] = useState<number[] | undefined>();

  const [sheetUploadStarted, setSheetUploadStarted] = useState(false);

  const shouldCreateTiledImageSheet =
    selectedFile !== undefined &&
    getFileExtension(selectedFile.name)?.toLowerCase() === "pdf" &&
    useImprovedImageResolution;

  const imageSheetTiledResolution = useMemo(
    () =>
      useImprovedImageResolution
        ? HIGH_IMAGE_SHEET_TILED_RESOLUTION
        : Math.max(MAX_ALLOWED_IMG_HEIGHT, MAX_ALLOWED_IMG_WIDTH),
    [useImprovedImageResolution],
  );

  const dispatch = useAppDispatch();
  const uploadFile = useFileUploaderWithPromise();
  const { projectApiClient: projectApi, coreApiClient: coreApi } =
    useApiClientContext();

  const uploadedFileDataRef = useRef<SourcePdfInfoNoPageNumber | undefined>(
    undefined,
  );
  const uploadPromiseRef =
    useRef<Promise<SourcePdfInfoNoPageNumber | undefined>>(undefined);

  const getOriginalPdfFileUploadedData: GetSourcePdfFileData =
    useCallback(() => {
      if (
        !selectedFile ||
        getFileExtension(selectedFile.name)?.toLowerCase() !== "pdf"
      ) {
        return Promise.resolve(undefined);
      }

      // Return cached data if already uploaded
      if (uploadedFileDataRef.current) {
        return Promise.resolve(uploadedFileDataRef.current);
      }

      // Return existing upload promise if one is in progress
      if (uploadPromiseRef.current) {
        return uploadPromiseRef.current;
      }

      // Upload and cache the result
      const uploadPromise = uploadFile({
        file: selectedFile,
        uploadElementType: UploadElementType.none,
        projectId: projectApi.projectId,
        coreApiClient: coreApi,
      }).then((uploadedData) => {
        uploadedFileDataRef.current = {
          downloadUrl: uploadedData.downloadUrl,
          md5Hash: uploadedData.md5,
          size: selectedFile.size,
          name: selectedFile.name,
        };
        uploadPromiseRef.current = undefined;
        return uploadedFileDataRef.current;
      });

      uploadPromiseRef.current = uploadPromise;
      return uploadPromise;
    }, [coreApi, projectApi.projectId, selectedFile, uploadFile]);

  const onCreateSheet = useCallback(async () => {
    // If the name is empty, do not trigger anything but force the
    // UI to display the error to the user
    if (!inputName.length) {
      setInputName("");
      return;
    }

    assert(selectedFile, "Expecting selectedFile to be defined");
    const numberOfPages = selectedPages?.length ? selectedPages.length : 1;
    Analytics.track<ImportAreaSheetProperties>(
      existingArea
        ? EventType.importSheetConfirmImport
        : EventType.importAreaConfirmImport,
      {
        numberOfSheetsSelected: numberOfPages,
        numberOfSheetsInDocument: numberOfPdfPages ?? 1,
        isCropActive: !!crop,
        isRotationActive: rotation !== 0,
        fileType: getFileExtension(selectedFile.name) ?? "",
        isImprovedResolution: useImprovedImageResolution,
      },
    );
    setSheetUploadStarted(true);
    dispatch(setNumberOfSheets(numberOfPages));
    dispatch(resetAreaImgSheets());
    if (shouldCreateTiledImageSheet) {
      if (selectedPages?.length) {
        for (let index = 0; index < selectedPages.length; index++) {
          await createImageSheetTiled(
            selectedFile,
            imageSheetTiledResolution,
            IMAGE_SHEET_TILES_SIZE,
            formatSheetName(inputName, selectedPages.length, index),
            selectedPages[index],
            getOriginalPdfFileUploadedData,
          );
        }
      } else {
        await createImageSheetTiled(
          selectedFile,
          imageSheetTiledResolution,
          IMAGE_SHEET_TILES_SIZE,
          inputName,
          previewedPdfPage,
          getOriginalPdfFileUploadedData,
        );
      }
    } else if (selectedPages?.length) {
      for (let index = 0; index < selectedPages.length; index++) {
        await createSheet(
          selectedFile,
          formatSheetName(inputName, selectedPages.length, index),
          selectedPages[index],
          getOriginalPdfFileUploadedData,
        );
      }
    } else {
      await createSheet(
        selectedFile,
        inputName,
        previewedPdfPage,
        getOriginalPdfFileUploadedData,
      );
    }
  }, [
    inputName,
    selectedFile,
    selectedPages,
    existingArea,
    numberOfPdfPages,
    crop,
    rotation,
    useImprovedImageResolution,
    dispatch,
    shouldCreateTiledImageSheet,
    setInputName,
    createImageSheetTiled,
    imageSheetTiledResolution,
    getOriginalPdfFileUploadedData,
    previewedPdfPage,
    createSheet,
  ]);

  const shouldShowPdfThumbnails = useMemo(
    () => selectedFile && numberOfPdfPages && numberOfPdfPages > 1,
    [selectedFile, numberOfPdfPages],
  );

  useEffect(() => {
    // Reset the selected pages when a new file is selected
    setSelectedPages([]);
    // Reset cached upload data when file changes
    uploadedFileDataRef.current = undefined;
    uploadPromiseRef.current = undefined;
  }, [selectedFile]);

  const allPagesAreSelected =
    numberOfPdfPages && selectedPages?.length === numberOfPdfPages;

  const toggleAllSelection = useCallback(() => {
    if (allPagesAreSelected) {
      Analytics.track(
        existingArea
          ? EventType.importSheetDeselectAll
          : EventType.importAreaDeselectAll,
      );
      setSelectedPages([]);
    } else {
      Analytics.track(
        existingArea
          ? EventType.importSheetSelectAll
          : EventType.importAreaSelectAll,
      );
      setSelectedPages(
        Array.from({ length: numberOfPdfPages ?? 0 }, (_, i) => i + 1),
      );
    }
  }, [allPagesAreSelected, existingArea, numberOfPdfPages]);

  const addToSelection = useCallback(
    (pageNumber: number) => {
      if (!selectedPages) {
        Analytics.track(
          existingArea
            ? EventType.importSheetSelectPage
            : EventType.importAreaSelectPage,
        );
        setSelectedPages([pageNumber]);
        return;
      }
      const index = selectedPages.indexOf(pageNumber);
      if (index === -1) {
        Analytics.track(
          existingArea
            ? EventType.importSheetSelectPage
            : EventType.importAreaSelectPage,
        );
        setSelectedPages([...selectedPages, pageNumber]);
      } else {
        Analytics.track(
          existingArea
            ? EventType.importSheetDeselectPage
            : EventType.importAreaDeselectPage,
        );
        setSelectedPages((prev) => prev?.filter((p) => p !== pageNumber));
      }
    },
    [existingArea, selectedPages],
  );

  const onPagesInputSelection = useCallback(
    (pages: number[] | undefined) => {
      Analytics.track(
        existingArea
          ? EventType.importSheetTextInputSelection
          : EventType.importAreaTextInputSelection,
      );
      setSelectedPages(pages);
    },
    [existingArea],
  );

  return (
    <Stack sx={{ ...sx, marginBottom: 4 }}>
      {isFirstSheet && (
        <FaroText variant="heading20" sx={{ mb: "0.875rem" }}>
          Import first sheet
        </FaroText>
      )}

      {isImageOversized && (
        <Alert
          title="Image will be automatically rescaled."
          description="The image exceeds the maximum supported resolution. The quality might be affected."
          variant="warning"
        />
      )}

      <SheetUploadControls
        file={previewImageFile}
        uploadStarted={sheetUploadStarted}
        disabled={sheetUploadStarted}
        inputName={inputName}
        inputNameError={inputNameError}
        onSetInputName={setInputName}
        numberOfPdfPages={numberOfPdfPages}
        canImageResolutionBeImproved={
          getFileExtension(selectedFile?.name) === "pdf"
        }
        useImprovedImageResolution={useImprovedImageResolution}
        onCancelUpload={cancelUpload}
        onCreateSheet={onCreateSheet}
        onSetUseImprovedImageResolution={setUseImprovedImageResolution}
        selectedPages={selectedPages}
        onSelect={onPagesInputSelection}
      />

      <Box
        component="div"
        sx={{ mt: "24px", width: "100%", bgcolor: grey[100] }}
      >
        {previewImageFile ? (
          <Stack
            direction="row"
            sx={{
              width: "100%",
              overflow: "hidden",
            }}
          >
            {shouldShowPdfThumbnails && selectedFile && (
              <Stack
                sx={{
                  width: "20%",
                }}
              >
                <Stack
                  direction="row"
                  sx={{ alignItems: "flex-end", justifyContent: "flex-end" }}
                >
                  <FaroButton
                    variant="ghost"
                    size="m"
                    sx={{
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "inherit",
                      },
                      mr: "3%",
                    }}
                    onClick={toggleAllSelection}
                  >
                    <FaroText
                      variant="bodyM"
                      fontWeight="Bold"
                      sx={{ color: blue[500] }}
                    >
                      {allPagesAreSelected ? "Deselect all" : "Select all"}
                    </FaroText>
                  </FaroButton>
                </Stack>
                <PdfPageThumbnailList
                  pdfFile={selectedFile}
                  onUpdatePreviewPage={onUpdateSelectedPdfPage}
                  selectedPdfPages={selectedPages}
                  onAddToSelection={addToSelection}
                  previewedPageNumber={previewedPdfPage}
                />
              </Stack>
            )}
            <SheetImagePreview
              file={previewImageFile}
              rotation={rotation}
              setRotation={setRotation}
              setCrop={setCrop}
              widthToHeightRatio={widthToHeightRatio}
              disabled={sheetUploadStarted}
              width={shouldShowPdfThumbnails ? "80%" : "100%"}
            />
          </Stack>
        ) : (
          <OpenImageFileSelectControl onSetFile={setFile} />
        )}
      </Box>

      {!previewImageFile && hasDefaultGrid && (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            mt: "1rem",
            gap: 2,
          }}
        >
          <FaroText variant="bodyM" sx={{ color: "gray850" }}>
            Don't have an image on hand? Click on the <b>Use grid</b> button to
            get started right away.
          </FaroText>
          <FaroButton
            aria-label="use grid"
            variant="ghost"
            size="m"
            onClick={() => {
              Analytics.track<CreateAreaEventProperties>(
                EventType.setFloorImage,
                {
                  via: "default-grid",
                  extension: "",
                },
              );

              setDefaultGrid();
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            Use grid
          </FaroButton>
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Format multiple pages sheet name based on the max page number and page index
 *
 * @param baseName the base name
 * @param numberOfPages total number of pages to upload
 * @param pageIndex the 0 based index of page to upload
 * @returns the formatted name in the form of "baseName + dash + (index+1) with padding zeros"
 * The padding zeros are used to make index number have the same number of digits as numberOfPages
 */
function formatSheetName(
  baseName: string,
  numberOfPages: number,
  pageIndex: number,
): string {
  return numberOfPages > 1
    ? `${baseName} - ${String(pageIndex + 1).padStart(numberOfPages.toString().length, "0")}`
    : baseName;
}
